import { useState, useMemo } from 'react';
import { useCustomItemPicker } from '../../hooks/useCustomItemPicker';
import PropTypes from 'prop-types';
import OrderItemsTable from './OrderItemsTable';
import { formatDate, formatDateTime, formatCurrency } from '../../utils/formatters';
import {
  STATUS_COLORS,
  STATUS_LABELS,
  DELIVERY_STATUS_SEQUENCE,
  PICKUP_STATUS_SEQUENCE,
  STATUS_TRACK_LABELS,
  FULFILLMENT_LABELS,
} from '../../utils/orderConstants';
import { SAMPLE_ELIGIBLE_UNITS } from '../../utils/constants';
import './OrderDetail.css';

// Merge colors + labels into the shape this component needs locally
const STATUS_DISPLAY = Object.fromEntries(
  Object.keys(STATUS_COLORS).map((key) => [
    key,
    { label: STATUS_LABELS[key] || key, color: STATUS_COLORS[key] },
  ])
);

// ─── Edit form (rendered when editMode is true) ──────────────────────────────

const EditForm = ({
  order,
  addresses,
  addressesLoading,
  sessions,
  sessionsLoading,
  products,
  productsLoading,
  onSave,
  onCancel,
  isSaving,
  isEmployee,
}) => {
  const [fulfillmentType, setFulfillmentType] = useState(order.fulfillment_type || 'delivery');
  const [deliveryDate, setDeliveryDate] = useState(order.delivery_date || '');
  const [deliveryAddressId, setDeliveryAddressId] = useState(
    order.delivery_address_display?.id ?? order.delivery_address ?? null
  );
  const [sessionId, setSessionId] = useState(order.session ?? null);
  const [deliveryRegion, setDeliveryRegion] = useState(order.delivery_region || '');
  const [specialInstructions, setSpecialInstructions] = useState(order.special_instructions || '');
  const [shippingCharge, setShippingCharge] = useState(
    order.shipping_charge_dollars != null ? String(parseFloat(order.shipping_charge_dollars) || '') : ''
  );
  const [shippingMode, setShippingMode] = useState('dollar');

  // ── Line items ───────────────────────────────────────────
  const [items, setItems] = useState(() =>
    (order.items || []).map((item) => {
      const isCustom = !item.product_variant;
      return {
        id: item.id,
        isCustom,
        productVariantId: item.product_variant?.id,
        description: item.description || '',
        quantity: item.quantity,
        is_sample: item.is_sample || false,
        ...(isCustom ? {} : {
          product: {
            id: item.product_variant?.product_id,
            name: item.product_variant?.product_name,
          },
          variant: {
            id: item.product_variant?.id,
            variant_name: item.product_variant?.variant_name,
            sku: item.product_variant?.sku,
          },
        }),
        unit_price: item.unit_price_dollars,
        subtotal_dollars: item.subtotal_dollars,
      };
    })
  );

  // ── Items subtotal (for % shipping calc) ────────────────
  const itemsSubtotal = useMemo(() =>
    items.reduce((sum, i) => {
      if (i.is_sample) return sum;
      const price = parseFloat(i.unit_price) || 0;
      const qty = parseInt(i.quantity, 10) || 0;
      const sub = parseFloat(i.subtotal_dollars) || price * qty;
      return sum + sub;
    }, 0),
  [items]);

  // ── Computed shipping dollar amount ──────────────────────
  const shippingDollarValue = useMemo(() => {
    const val = parseFloat(shippingCharge) || 0;
    if (shippingMode === 'percent') return (val / 100) * itemsSubtotal;
    return val;
  }, [shippingCharge, shippingMode, itemsSubtotal]);

  // ── Product picker ───────────────────────────────────────
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [pickQuantity, setPickQuantity] = useState(1);
  const [pickIsSample, setPickIsSample] = useState(false);

  // ── Custom item picker ───────────────────────────────────
  const {
    customDesc, setCustomDesc,
    customQty, setCustomQty,
    customPrice, setCustomPrice,
    handleAdd: handleAddCustomItem,
    isValid: isCustomValid,
  } = useCustomItemPicker();

  const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
  const variants = useMemo(
    () =>
      [...(selectedProduct?.variants ?? [])]
        .filter((v) => !pickIsSample || SAMPLE_ELIGIBLE_UNITS.has(v.unit ?? v.variant_name))
        .sort((a, b) => (parseFloat(a.variant_name) || 0) - (parseFloat(b.variant_name) || 0)),
    [selectedProduct, pickIsSample]
  );
  const selectedVariant = variants.find((v) => String(v.id) === String(selectedVariantId));

  const isPickup = fulfillmentType === 'pickup';

  const handleAddItem = () => {
    if (!selectedVariant) return;
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.productVariantId === selectedVariant.id && i.is_sample === pickIsSample
      );
      if (existing) {
        return prev.map((i) =>
          i.productVariantId === selectedVariant.id && i.is_sample === pickIsSample
            ? { ...i, quantity: i.quantity + pickQuantity }
            : i
        );
      }
      return [
        ...prev,
        {
          id: `new-${selectedVariant.id}-${pickIsSample ? 'sample' : 'reg'}`,
          productVariantId: selectedVariant.id,
          quantity: pickQuantity,
          is_sample: pickIsSample,
          product: { id: selectedProduct.id, name: selectedProduct.name },
          variant: {
            id: selectedVariant.id,
            variant_name: selectedVariant.variant_name,
            sku: selectedVariant.sku,
          },
          unit_price: pickIsSample ? 0 : (selectedVariant.price_dollars ?? selectedVariant.price ?? 0),
        },
      ];
    });
    setSelectedProductId('');
    setSelectedVariantId('');
    setPickQuantity(1);
  };

  const handleUpdateQuantity = (itemId, newQty) => {
    if (newQty < 1) return;
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, quantity: newQty } : i)));
  };

  const handleUpdatePrice = (itemId, value) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, unit_price: value } : i)));
  };

  const handleRemoveItem = (itemId) => {
    setItems((prev) => prev.filter((i) => i.id !== itemId));
  };


  const [formError, setFormError] = useState(null);

  // ── Original snapshot for dirty-checking ────────────────
  const originalItems = useMemo(
    () =>
      (order.items || []).map((item) => ({
        isCustom: !item.product_variant,
        productVariantId: item.product_variant?.id,
        description: item.description || '',
        quantity: item.quantity,
        is_sample: item.is_sample || false,
        unit_price: item.unit_price_dollars,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // intentionally fixed to mount-time value
  );

  const itemsChanged = useMemo(() => {
    if (items.length !== originalItems.length) return true;
    const sortKey = (i) => i.isCustom ? `custom-${i.id}-${i.description}` : `${i.productVariantId}-${i.is_sample}`;
    const sorted = [...items].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    const origSorted = [...originalItems].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    return sorted.some(
      (item, idx) =>
        item.productVariantId !== origSorted[idx].productVariantId ||
        item.description !== origSorted[idx].description ||
        item.quantity !== origSorted[idx].quantity ||
        item.is_sample !== origSorted[idx].is_sample ||
        parseFloat(item.unit_price ?? 0) !== parseFloat(origSorted[idx].unit_price ?? 0)
    );
  }, [items, originalItems]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (items.length === 0) {
      setFormError('Order must have at least one item.');
      return;
    }
    setFormError(null);

    const originalFulfillment = order.fulfillment_type || 'delivery';
    const originalAddressId =
      order.delivery_address_display?.id ?? order.delivery_address ?? null;

    const payload = {
      fulfillment_type: fulfillmentType,
      delivery_date: deliveryDate || null,
      session: sessionId != null ? Number(sessionId) : null,
    };

    // Only send delivery_region if it changed
    if (deliveryRegion !== (order.delivery_region || '')) {
      payload.delivery_region = deliveryRegion;
    }

    // Only send special_instructions if it changed (avoid clearing with default "")
    if (specialInstructions !== (order.special_instructions || '')) {
      payload.special_instructions = specialInstructions;
    }

    // Only send delivery_address when it's meaningful:
    //  - switching to pickup → explicitly clear it
    //  - staying delivery and address changed → send new value
    if (isPickup) {
      if (originalFulfillment !== 'pickup') {
        // Switched to pickup — clear the address
        payload.delivery_address = null;
      }
      // else: already pickup, no address to touch
    } else {
      // delivery mode
      const newAddrId = deliveryAddressId != null ? Number(deliveryAddressId) : null;
      if (newAddrId !== (originalAddressId != null ? Number(originalAddressId) : null)) {
        payload.delivery_address = newAddrId;
      }
    }

    // Only send items if they actually changed — avoids silent re-pricing
    if (itemsChanged) {
      payload.items = items.map((i) => {
        if (i.isCustom) {
          return {
            description: i.description,
            quantity: i.quantity,
            unit_price_cents: Math.round(parseFloat(i.unit_price) * 100),
          };
        }
        const item = {
          product_variant_id: i.productVariantId,
          quantity: i.quantity,
          is_sample: i.is_sample || false,
        };
        if (isEmployee && !i.is_sample && i.unit_price != null && i.unit_price !== '') {
          const cents = Math.round(parseFloat(i.unit_price) * 100);
          if (!isNaN(cents)) item.unit_price_cents = cents;
        }
        return item;
      });
    }

    if (isEmployee) {
      const shippingCents = Math.round(shippingDollarValue * 100);
      const originalCents = order.shipping_charge_cents ?? 0;
      if (shippingCents !== originalCents) {
        payload.shipping_charge_cents = shippingCents;
      }
    }

    onSave(payload);
  };

  return (
    <form className="order-edit-form" onSubmit={handleSubmit}>

      {formError && <p className="order-edit-form__error">{formError}</p>}

      {/* ── Product picker ──────────────────────────────── */}
      <div className="order-edit-form__section">
        <p className="order-detail__section-eyebrow">Line Items</p>

        {productsLoading ? (
          <p className="order-edit-form__loading">Loading products…</p>
        ) : (
          <div className="order-edit-form__picker">
            <label className="order-edit-form__sample-label">
              <input
                type="checkbox"
                className="order-edit-form__sample-checkbox"
                checked={pickIsSample}
                onChange={(e) => {
                  setPickIsSample(e.target.checked);
                  setSelectedVariantId('');
                }}
              />
              Sample
            </label>

            <select
              className="order-edit-form__select"
              value={selectedProductId}
              onChange={(e) => { setSelectedProductId(e.target.value); setSelectedVariantId(''); setPickQuantity(1); }}
              aria-label="Select product to add"
            >
              <option value="">— Add product —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <select
              className="order-edit-form__select"
              value={selectedVariantId}
              onChange={(e) => setSelectedVariantId(e.target.value)}
              disabled={!selectedProduct}
              aria-label="Select variant"
            >
              <option value="">— Size —</option>
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.variant_name}
                  {pickIsSample
                    ? ' — Free'
                    : (v.price_dollars ?? v.price) != null
                      ? ` — ${formatCurrency(v.price_dollars ?? v.price)}`
                      : ''}
                </option>
              ))}
            </select>

            <div className="order-edit-form__qty-wrap">
              <button
                type="button"
                className="order-edit-form__qty-btn"
                onClick={() => setPickQuantity((q) => Math.max(1, q - 1))}
                disabled={pickQuantity <= 1}
                aria-label="Decrease quantity"
              >−</button>
              <span className="order-edit-form__qty-val">{pickQuantity}</span>
              <button
                type="button"
                className="order-edit-form__qty-btn"
                onClick={() => setPickQuantity((q) => q + 1)}
                aria-label="Increase quantity"
              >+</button>
            </div>

            <button
              type="button"
              className="order-edit-form__add-btn"
              onClick={handleAddItem}
              disabled={!selectedVariant}
            >
              Add
            </button>
          </div>
        )}

        {/* ── Custom item picker ──────────────────────────── */}
        <div className="order-edit-form__custom-picker">
          <input
            type="text"
            className="order-edit-form__custom-desc"
            placeholder="Custom item description"
            value={customDesc}
            onChange={(e) => setCustomDesc(e.target.value)}
            maxLength={255}
            aria-label="Custom item description"
          />
          <div className="order-edit-form__qty-wrap">
            <button
              type="button"
              className="order-edit-form__qty-btn"
              onClick={() => setCustomQty((q) => Math.max(1, q - 1))}
              disabled={customQty <= 1}
              aria-label="Decrease quantity"
            >−</button>
            <span className="order-edit-form__qty-val">{customQty}</span>
            <button
              type="button"
              className="order-edit-form__qty-btn"
              onClick={() => setCustomQty((q) => q + 1)}
              aria-label="Increase quantity"
            >+</button>
          </div>
          <div className="order-edit-form__price-wrap">
            <span className="order-edit-form__price-prefix">$</span>
            <input
              type="number"
              className="order-edit-form__price-input"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              aria-label="Custom item price"
            />
          </div>
          <button
            type="button"
            className="order-edit-form__add-btn"
            onClick={() => handleAddCustomItem((item) => setItems((prev) => [...prev, { ...item, is_sample: false }]))}
            disabled={!isCustomValid}
          >
            Add Custom
          </button>
        </div>

        <OrderItemsTable
          items={items}
          editable
          isEmployee={isEmployee}
          onUpdateQuantity={handleUpdateQuantity}
          onUpdatePrice={handleUpdatePrice}
          onRemoveItem={handleRemoveItem}
          shippingChargeDollars={shippingDollarValue}
          discountCents={order.discount_cents || 0}
        />
      </div>

      <hr className="order-detail__rule" />

      {/* ── Delivery details ────────────────────────────── */}
      <div className="order-edit-form__section">
        <p className="order-detail__section-eyebrow">Delivery Details</p>

        <div className="order-edit-form__fields">

          {/* Fulfillment toggle */}
          <div className="order-edit-form__field">
            <label className="order-edit-form__label">Fulfillment Type</label>
            <div className="order-edit-form__toggle" role="group" aria-label="Fulfillment type">
              <button
                type="button"
                className={`order-edit-form__toggle-btn${fulfillmentType === 'delivery' ? ' order-edit-form__toggle-btn--active' : ''}`}
                onClick={() => setFulfillmentType('delivery')}
              >
                {FULFILLMENT_LABELS.delivery}
              </button>
              <button
                type="button"
                className={`order-edit-form__toggle-btn${fulfillmentType === 'pickup' ? ' order-edit-form__toggle-btn--active' : ''}`}
                onClick={() => setFulfillmentType('pickup')}
              >
                {FULFILLMENT_LABELS.pickup}
              </button>
            </div>
          </div>

          {/* Session */}
          <div className="order-edit-form__field">
            <label className="order-edit-form__label" htmlFor="edit-session">Mill Session</label>
            {sessionsLoading ? (
              <p className="order-edit-form__loading">Loading sessions…</p>
            ) : sessions.length === 0 ? (
              <p className="order-edit-form__hint">No sessions available.</p>
            ) : (
              <select
                id="edit-session"
                className="order-edit-form__select order-edit-form__select--full"
                value={sessionId ?? ''}
                onChange={(e) => setSessionId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— No session —</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* Delivery address — only for delivery */}
          {!isPickup && (
            <div className="order-edit-form__field">
              <label className="order-edit-form__label" htmlFor="edit-address">Delivery Address</label>
              {addressesLoading ? (
                <p className="order-edit-form__loading">Loading addresses…</p>
              ) : addresses.length === 0 ? (
                <p className="order-edit-form__hint">No shipping addresses on file.</p>
              ) : (
                <select
                  id="edit-address"
                  className="order-edit-form__select order-edit-form__select--full"
                  value={deliveryAddressId ?? ''}
                  onChange={(e) => setDeliveryAddressId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">— Select address —</option>
                  {addresses.map((addr) => (
                    <option key={addr.id} value={addr.id}>
                      {addr.street_address}
                      {addr.street_address_2 ? `, ${addr.street_address_2}` : ''},{' '}
                      {addr.city}, {addr.state} {addr.zip_code}
                      {addr.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Delivery date */}
          <div className="order-edit-form__field">
            <label className="order-edit-form__label" htmlFor="edit-delivery-date">
              {isPickup ? 'Pickup Date' : 'Delivery Date'}
              <span className="order-edit-form__label-optional"> — optional</span>
            </label>
            <input
              id="edit-delivery-date"
              type="date"
              className="order-edit-form__input"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
            />
          </div>

          {/* Delivery region — employee only */}
          {isEmployee && (
            <div className="order-edit-form__field">
              <label className="order-edit-form__label" htmlFor="edit-delivery-region">
                Delivery Region
                <span className="order-edit-form__label-optional"> — optional</span>
              </label>
              <select
                id="edit-delivery-region"
                className="order-edit-form__input"
                value={deliveryRegion}
                onChange={(e) => setDeliveryRegion(e.target.value)}
              >
                <option value="">— none —</option>
                <option value="local">local</option>
                <option value="cville">cville</option>
                <option value="east">east</option>
                <option value="north">north</option>
                <option value="west">west</option>
                <option value="south">south</option>
                <option value="far">far</option>
                <option value="valley">valley</option>
              </select>
            </div>
          )}

          {/* Shipping charge — employee only */}
          {isEmployee && (
            <div className="order-edit-form__field">
              <label className="order-edit-form__label" htmlFor="edit-shipping-charge">
                Shipping Charge
                <span className="order-edit-form__label-optional"> — optional</span>
              </label>
              <div className="order-edit-form__shipping-row">
                <div className="order-edit-form__shipping-toggle">
                  <button
                    type="button"
                    className={`order-edit-form__shipping-mode${shippingMode === 'dollar' ? ' order-edit-form__shipping-mode--active' : ''}`}
                    onClick={() => { setShippingMode('dollar'); setShippingCharge(''); }}
                  >$</button>
                  <button
                    type="button"
                    className={`order-edit-form__shipping-mode${shippingMode === 'percent' ? ' order-edit-form__shipping-mode--active' : ''}`}
                    onClick={() => { setShippingMode('percent'); setShippingCharge(''); }}
                  >%</button>
                </div>
                <input
                  id="edit-shipping-charge"
                  type="number"
                  min="0"
                  max={shippingMode === 'percent' ? 100 : undefined}
                  step={shippingMode === 'percent' ? '0.1' : '0.01'}
                  className="order-edit-form__input order-edit-form__input--shipping"
                  value={shippingCharge}
                  onChange={(e) => setShippingCharge(e.target.value)}
                  placeholder={shippingMode === 'percent' ? '0.0' : '0.00'}
                />
              </div>
              {shippingMode === 'percent' && parseFloat(shippingCharge) > 0 && (
                <p className="order-edit-form__shipping-preview">
                  = {formatCurrency(shippingDollarValue)}
                </p>
              )}
            </div>
          )}

          {/* Special instructions */}
          <div className="order-edit-form__field">
            <label className="order-edit-form__label" htmlFor="edit-instructions">
              Special Instructions
              <span className="order-edit-form__label-optional"> — optional</span>
            </label>
            <textarea
              id="edit-instructions"
              className="order-edit-form__textarea"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Any notes for this order…"
              rows={3}
            />
          </div>

        </div>
      </div>

      {/* ── Actions ─────────────────────────────────────── */}
      <div className="order-edit-form__actions">
        <button
          type="submit"
          className="order-edit-form__save-btn"
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save Changes'}
        </button>
        <button
          type="button"
          className="order-edit-form__cancel-btn"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
      </div>

    </form>
  );
};

// ─── Main OrderDetail component ───────────────────────────────────────────────

const OrderDetail = ({
  order,
  isEmployee = false,
  editMode = false,
  onStatusUpdate,
  onNotesUpdate,
  onSaveEdit,
  onCancelEdit,
  isSavingEdit = false,
  addresses = [],
  addressesLoading = false,
  sessions = [],
  sessionsLoading = false,
  products = [],
  productsLoading = false,
}) => {
  if (!order) {
    return (
      <div className="order-detail order-detail--empty">
        <p>Order not found.</p>
      </div>
    );
  }

  const {
    order_number,
    status,
    fulfillment_type,
    session_detail,
    delivery_date,
    delivery_address,
    delivery_address_display,
    delivery_region,
    special_instructions,
    internal_notes,
    items,
    created_at,
    updated_at,
    company,
    company_detail,
    created_by_detail,
  } = order;

  const addressDisplay =
    delivery_address_display ||
    (delivery_address && typeof delivery_address === 'object' ? delivery_address : null);

  const isPickup = fulfillment_type === 'pickup';
  const STATUS_SEQUENCE = isPickup ? PICKUP_STATUS_SEQUENCE : DELIVERY_STATUS_SEQUENCE;

  const isCancelled = status === 'cancelled';
  const currentStatusIdx = STATUS_SEQUENCE.indexOf(status);
  const progressPercent = !isCancelled && currentStatusIdx >= 0
    ? (currentStatusIdx / (STATUS_SEQUENCE.length - 1)) * 100
    : 0;

  const statusInfo = STATUS_DISPLAY[status] || { label: status, color: '#9c8f7e' };

  return (
    <div className={`order-detail${editMode ? ' order-detail--editing' : ''}`}>

      {/* ── Document header ─────────────────────────────────── */}
      <div className="order-detail__header">
        <div className="order-detail__header-left">
          <p className="order-detail__eyebrow">{company_detail?.company_name || company?.company_name || 'Deep Roots Milling'}</p>
          <h2 className="order-detail__title">{order_number}</h2>
          <p className="order-detail__timestamp">Placed on {formatDateTime(created_at)}</p>
        </div>
        <div
          className="order-detail__status-stamp"
          style={{ color: statusInfo.color, borderColor: `${statusInfo.color}40` }}
        >
          {statusInfo.label}
        </div>
      </div>

      <hr className="order-detail__rule order-detail__rule--gradient" />

      {/* ── Status progress track (read-only view only) ──────── */}
      {!editMode && !isCancelled && (
        <div className="order-detail__track-wrap">
          <div className="order-detail__track">
            <div className="order-detail__track-rail">
              <div
                className="order-detail__track-fill"
                style={{ transform: `scaleX(${progressPercent / 100})` }}
              />
            </div>
            {STATUS_SEQUENCE.map((stepKey, i) => {
              const state = i < currentStatusIdx ? 'done' : i === currentStatusIdx ? 'active' : 'upcoming';
              return (
                <div key={stepKey} className={`order-detail__track-step order-detail__track-step--${state}`}>
                  <div className="order-detail__track-dot" />
                  <span className="order-detail__track-label">
                    {STATUS_TRACK_LABELS[stepKey]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!editMode && isCancelled && (
        <div className="order-detail__cancelled">
          This order has been cancelled
        </div>
      )}

      {!editMode && <hr className="order-detail__rule" />}

      {/* ── Edit form or read-only view ───────────────────────── */}
      {editMode ? (
        <EditForm
          order={order}
          addresses={addresses}
          addressesLoading={addressesLoading}
          sessions={sessions}
          sessionsLoading={sessionsLoading}
          products={products}
          productsLoading={productsLoading}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
          isSaving={isSavingEdit}
          isEmployee={isEmployee}
        />
      ) : (
        <>
          {/* ── Metadata ─────────────────────────────────────────── */}
          <dl className="order-detail__meta">
            {fulfillment_type && (
              <div className="order-detail__meta-row">
                <dt className="order-detail__meta-label">Fulfillment</dt>
                <dd className="order-detail__meta-value">
                  <span className={`order-detail__fulfillment-badge order-detail__fulfillment-badge--${fulfillment_type}`}>
                    {FULFILLMENT_LABELS[fulfillment_type] || fulfillment_type}
                  </span>
                </dd>
              </div>
            )}
            {session_detail && (
              <div className="order-detail__meta-row">
                <dt className="order-detail__meta-label">Mill Session</dt>
                <dd className="order-detail__meta-value">
                  {session_detail.label} (Delivery begins {session_detail.delivery_start})
                </dd>
              </div>
            )}
            {delivery_date && (
              <div className="order-detail__meta-row">
                <dt className="order-detail__meta-label">{isPickup ? 'Pickup Date' : 'Delivery Date'}</dt>
                <dd className="order-detail__meta-value">{formatDate(delivery_date)}</dd>
              </div>
            )}
            {addressDisplay && (
              <div className="order-detail__meta-row">
                <dt className="order-detail__meta-label">Delivery Address</dt>
                <dd className="order-detail__meta-value order-detail__meta-value--stack">
                  <span>{addressDisplay.street_address}</span>
                  {addressDisplay.street_address_2 && (
                    <span>{addressDisplay.street_address_2}</span>
                  )}
                  <span>
                    {addressDisplay.city}, {addressDisplay.state} {addressDisplay.zip_code}
                  </span>
                  {addressDisplay.notes && (
                    <span className="order-detail__address-notes">
                      <span className="order-detail__address-notes-label">Notes</span>
                      {addressDisplay.notes}
                    </span>
                  )}
                </dd>
              </div>
            )}
            {isEmployee && (
              <div className="order-detail__meta-row">
                <dt className="order-detail__meta-label">Delivery Region</dt>
                <dd className="order-detail__meta-value">{delivery_region || '—'}</dd>
              </div>
            )}
            {isEmployee && company_detail && (
              <>
                <div className="order-detail__meta-row">
                  <dt className="order-detail__meta-label">Company</dt>
                  <dd className="order-detail__meta-value">{company_detail.company_name}</dd>
                </div>
                {company_detail.phone && (
                  <div className="order-detail__meta-row">
                    <dt className="order-detail__meta-label">Phone</dt>
                    <dd className="order-detail__meta-value">{company_detail.phone}</dd>
                  </div>
                )}
                {created_by_detail && (
                  <div className="order-detail__meta-row">
                    <dt className="order-detail__meta-label">Ordered By</dt>
                    <dd className="order-detail__meta-value">
                      {created_by_detail.first_name} {created_by_detail.last_name}
                    </dd>
                  </div>
                )}
              </>
            )}
            {updated_at && (
              <div className="order-detail__meta-row">
                <dt className="order-detail__meta-label">Last Updated</dt>
                <dd className="order-detail__meta-value">{formatDateTime(updated_at)}</dd>
              </div>
            )}
          </dl>

          <hr className="order-detail__rule" />

          {/* ── Line items ───────────────────────────────────────── */}
          <div className="order-detail__items">
            <p className="order-detail__section-eyebrow">Line Items</p>
            <OrderItemsTable
              items={items || []}
              shippingChargeDollars={order.shipping_charge_dollars}
              discountCents={order.discount_cents || 0}
            />
          </div>

          {/* ── Special Instructions ─────────────────────────────── */}
          {special_instructions && (
            <>
              <hr className="order-detail__rule" />
              <div className="order-detail__block">
                <p className="order-detail__section-eyebrow">Special Instructions</p>
                <p className="order-detail__block-text">{special_instructions}</p>
              </div>
            </>
          )}

          {/* ── Internal Notes (employee) ────────────────────────── */}
          {isEmployee && (
            <>
              <hr className="order-detail__rule" />
              <div className="order-detail__block">
                <p className="order-detail__section-eyebrow">Internal Notes</p>
                {onNotesUpdate ? (
                  <textarea
                    className="order-detail__textarea"
                    value={internal_notes || ''}
                    onChange={(e) => onNotesUpdate(e.target.value)}
                    placeholder="Add internal notes…"
                    rows={4}
                  />
                ) : (
                  <p className="order-detail__block-text">
                    {internal_notes || '—'}
                  </p>
                )}
              </div>
            </>
          )}

          {/* ── Status update (employee) ─────────────────────────── */}
          {isEmployee && onStatusUpdate && (
            <>
              <hr className="order-detail__rule" />
              <div className="order-detail__block">
                <p className="order-detail__section-eyebrow">Update Status</p>
                <select
                  className="order-detail__status-select"
                  value={status}
                  onChange={(e) => onStatusUpdate(e.target.value)}
                >
                  {STATUS_SEQUENCE.map(s => (
                    <option key={s} value={s}>{STATUS_TRACK_LABELS[s] || STATUS_LABELS[s]}</option>
                  ))}
                  {/* Ensure current status is always selectable even if not in sequence */}
                  {!STATUS_SEQUENCE.includes(status) && status !== 'cancelled' && (
                    <option value={status}>{STATUS_TRACK_LABELS[status] || STATUS_LABELS[status] || status}</option>
                  )}
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </>
          )}
        </>
      )}

    </div>
  );
};

OrderDetail.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_number: PropTypes.string,
    status: PropTypes.string,
    fulfillment_type: PropTypes.string,
    delivery_date: PropTypes.string,
    delivery_region: PropTypes.string,
    delivery_address: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    delivery_address_display: PropTypes.shape({
      id: PropTypes.number,
      street_address: PropTypes.string,
      street_address_2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip_code: PropTypes.string,
    }),
    special_instructions: PropTypes.string,
    internal_notes: PropTypes.string,
    items: PropTypes.array,
    shipping_charge_cents: PropTypes.number,
    shipping_charge_dollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_amount_dollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    session: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    session_detail: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      mill_start: PropTypes.string,
      delivery_start: PropTypes.string,
    }),
    company: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    company_detail: PropTypes.shape({
      id: PropTypes.number,
      company_name: PropTypes.string,
      phone: PropTypes.string,
    }),
    created_by: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    created_by_detail: PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      username: PropTypes.string,
    }),
  }),
  isEmployee: PropTypes.bool,
  editMode: PropTypes.bool,
  onStatusUpdate: PropTypes.func,
  onNotesUpdate: PropTypes.func,
  onSaveEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  isSavingEdit: PropTypes.bool,
  addresses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    street_address: PropTypes.string,
    street_address_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip_code: PropTypes.string,
    is_default: PropTypes.bool,
    address_type: PropTypes.string,
  })),
  addressesLoading: PropTypes.bool,
  sessions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
  })),
  sessionsLoading: PropTypes.bool,
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    variants: PropTypes.array,
  })),
  productsLoading: PropTypes.bool,
};

export default OrderDetail;
